// src/hooks/useAddressState.js
import { useCallback, useEffect, useState } from 'react';

/**
 * Manages address UI state + mapping for TomTom places.
 * - addrQuery: visible text in the input
 * - addressFieldsState: { street, city, region, country, zip, id? }
 * - applyTomTomPlace: maps TomTom place → your fields and updates both states
 */
export default function useAddressState(globalData) {
  const [addrQuery, setAddrQuery] = useState('');
  const [addressFieldsState, setAddressFieldsState] = useState({
    street: '',
    city: '',
    region: '',
    country: '',
    zip: '',
    id: null,
  });

  // Initialize from globalData (first address)
  useEffect(() => {
    if (globalData?.address?.length > 0) {
      const a = globalData.address[0];
      setAddressFieldsState(a);
      const line1 = [a.street, a.city].filter(Boolean).join(', ');
      setAddrQuery(line1 || '');
    }
  }, [globalData]);

  // Local manual edits
  const handleAddressChange = useCallback((field, value) => {
    setAddressFieldsState((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Map TomTom suggestion → your schema
  const mapTomTomPlaceToFields = useCallback((place) => {
    const a = place?.address || {};
    const street = [a.streetNumber, a.streetName].filter(Boolean).join(' ');
    return {
      street,
      city: a.municipality || '',
      region: a.countrySubdivision || '',
      country: a.country || a.countryCode || '',
      zip: a.postalCode || '',
    };
  }, []);

  // Apply a TomTom place to both input and state
  const applyTomTomPlace = useCallback(
    (place) => {
      const mapped = mapTomTomPlaceToFields(place);
      const shortLabel = [mapped.street].filter(Boolean).join(' ');
      setAddrQuery(shortLabel || place.label || '');
      setAddressFieldsState((prev) => ({ ...prev, ...mapped }));
    },
    [mapTomTomPlaceToFields]
  );

  return {
    addrQuery,
    setAddrQuery,
    addressFieldsState,
    setAddressFieldsState,
    handleAddressChange,
    mapTomTomPlaceToFields,
    applyTomTomPlace,
  };
}
